// Admin — Arcade Question Bank manager (Pulse Power Quiz).
// Curate questions per Game -> Group (A/B/C/D) -> Level (1-10). Games randomly
// pick from this bank at play time. Each group gets a distinct question set.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api, mediaUrl } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ListChecks, Loader2, CheckCircle2, Settings, Save, Flame, ImagePlus, X, Upload, Download } from 'lucide-react';

const GROUPS = ['A', 'B', 'C', 'D'];
const GAMES = [
    { code: 'pulse_power_quiz', name: 'Pulse Power Quiz', levels: 10, minQ: 10, clear: (lv) => `+${lv * 10}/Q` },
    { code: 'pulse_crush', name: 'Pulse Crush', levels: 5, minQ: 3, clear: (lv) => `+${[0, 40, 80, 120, 160, 200][lv]}/correct` },
];

export default function AdminArcadeQuestions() {
    const [game, setGame] = useState('pulse_power_quiz');
    const gameMeta = GAMES.find((g) => g.code === game) || GAMES[0];
    const LEVELS = Array.from({ length: gameMeta.levels }, (_, i) => i + 1);
    const [group, setGroup] = useState('A');
    const [level, setLevel] = useState(1);
    const [counts, setCounts] = useState({});
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null); // question object or {} for new

    const loadCounts = useCallback(() => {
        api.get('/admin/arcade-questions/counts', { params: { game } }).then((r) => setCounts(r.data || {}));
    }, [game]);

    const loadQuestions = useCallback(() => {
        setLoading(true);
        api.get('/admin/arcade-questions', { params: { game, group, level } })
            .then((r) => setQuestions(r.data || []))
            .finally(() => setLoading(false));
    }, [game, group, level]);

    useEffect(() => { loadCounts(); }, [loadCounts]);
    useEffect(() => { loadQuestions(); }, [loadQuestions]);

    const onSaved = () => { setEditing(null); loadQuestions(); loadCounts(); };

    const remove = async (id) => {
        if (!window.confirm('Delete this question?')) return;
        await api.delete(`/admin/arcade-questions/${id}`);
        toast.success('Question deleted');
        loadQuestions(); loadCounts();
    };

    const bulkRef = useRef(null);
    const [bulkBusy, setBulkBusy] = useState(false);
    const onBulkUpload = async (e) => {
        const file = e.target.files?.[0];
        if (bulkRef.current) bulkRef.current.value = '';
        if (!file) return;
        setBulkBusy(true);
        try {
            const fd = new FormData();
            fd.append('game', game);
            fd.append('file', file);
            const r = await api.post('/admin/arcade-questions/bulk-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            const { inserted, error_count, errors } = r.data;
            if (inserted) toast.success(`${inserted} question${inserted === 1 ? '' : 's'} uploaded`);
            if (error_count) toast.error(`${error_count} row(s) skipped${errors?.[0] ? ` — e.g. row ${errors[0].row}: ${errors[0].error}` : ''}`);
            if (!inserted && !error_count) toast.error('No question rows found in the CSV');
            loadQuestions(); loadCounts();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Bulk upload failed');
        } finally { setBulkBusy(false); }
    };
    const downloadTemplate = () => {
        const csv = 'Level,Group,Question,Option_A,Option_B,Option_C,Option_D,Correct_Answer,Explanation\n'
            + '1,A,What does a fuse protect against?,Overcurrent,Low voltage,Static,Heat,A,A fuse breaks the circuit on overcurrent\n';
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob); const a = document.createElement('a');
        a.href = url; a.download = 'arcade-questions-template.csv'; a.click(); URL.revokeObjectURL(url);
    };

    return (
        <div data-testid="admin-arcade-questions-page">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                <div>
                    <h1 className="font-display font-extrabold text-3xl text-slate-900 flex items-center gap-2">
                        <ListChecks className="h-7 w-7 text-brand-red" /> Game Question Bank
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">Curate questions per group & level. Games randomly pick from here at play time.</p>
                </div>
                <select value={game} onChange={(e) => { setGame(e.target.value); setLevel(1); setGroup('A'); }}
                    className="px-3 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold" data-testid="arcade-game-select">
                    {GAMES.map((g) => <option key={g.code} value={g.code}>{g.name}</option>)}
                </select>
            </div>

            {/* Game settings */}
            {game === 'pulse_power_quiz' ? <PulseSettingsCard /> : <PulseCrushSettingsCard />}

            {/* Counts matrix */}
            <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-5 overflow-x-auto">
                <table className="w-full text-xs text-center min-w-[640px]">
                    <thead>
                        <tr className="text-slate-500">
                            <th className="py-1.5 px-2 text-left">Group \ Level</th>
                            {LEVELS.map((lv) => <th key={lv} className="py-1.5 px-2">L{lv}</th>)}
                            <th className="py-1.5 px-2">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {GROUPS.map((g) => {
                            const row = counts[g] || {};
                            const tot = Object.values(row).reduce((a, b) => a + b, 0);
                            return (
                                <tr key={g} className="border-t border-slate-100">
                                    <td className="py-1.5 px-2 text-left font-bold text-slate-700">Group {g}</td>
                                    {LEVELS.map((lv) => {
                                        const c = row[String(lv)] || 0;
                                        const ok = c >= gameMeta.minQ;
                                        return (
                                            <td key={lv} className="py-1.5 px-2">
                                                <button
                                                    onClick={() => { setGroup(g); setLevel(lv); }}
                                                    className={`w-8 h-7 rounded-md font-bold ${ok ? 'bg-emerald-100 text-emerald-700' : c > 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-400'} hover:ring-2 hover:ring-brand-yellow`}
                                                    data-testid={`count-${g}-${lv}`}>
                                                    {c}
                                                </button>
                                            </td>
                                        );
                                    })}
                                    <td className="py-1.5 px-2 font-extrabold text-slate-900">{tot}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <p className="text-[11px] text-slate-400 mt-2">Each play picks random questions — keep at least {gameMeta.minQ} per group/level (green) for full variety.</p>
            </div>

            {/* Group / Level filter + add */}
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div className="flex items-center gap-2">
                    <div className="flex gap-1 bg-slate-100 rounded-xl p-1" data-testid="arcade-group-tabs">
                        {GROUPS.map((g) => (
                            <button key={g} onClick={() => setGroup(g)} data-testid={`group-tab-${g}`}
                                className={`px-3 py-1.5 rounded-lg text-sm font-bold ${group === g ? 'bg-white shadow text-brand-black' : 'text-slate-500'}`}>
                                Group {g}
                            </button>
                        ))}
                    </div>
                    <select value={level} onChange={(e) => setLevel(Number(e.target.value))}
                        className="px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold" data-testid="arcade-level-select">
                        {LEVELS.map((lv) => <option key={lv} value={lv}>Level {lv} · {gameMeta.clear(lv)}</option>)}
                    </select>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={downloadTemplate} className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-sm font-semibold hover:bg-slate-50" data-testid="arcade-template-btn">
                        <Download className="h-4 w-4" /> Template
                    </button>
                    <button onClick={() => bulkRef.current?.click()} disabled={bulkBusy}
                        className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-brand-black text-brand-yellow text-sm font-semibold disabled:opacity-60" data-testid="arcade-bulk-upload-btn">
                        <Upload className="h-4 w-4" /> {bulkBusy ? 'Uploading…' : 'Bulk upload CSV'}
                    </button>
                    <input ref={bulkRef} type="file" accept=".csv,text/csv" onChange={onBulkUpload} className="hidden" data-testid="arcade-bulk-input" />
                    <button onClick={() => setEditing({ game_code: game, group, level, question: '', options: ['', '', '', ''], correct_index: 0, explanation: '' })}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-blue text-white font-semibold hover:bg-brand-blueDark" data-testid="arcade-add-question-btn">
                        <Plus className="h-4 w-4" /> Add Question
                    </button>
                </div>
            </div>

            {/* Questions list */}
            <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
                {loading ? (
                    <div className="py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
                ) : questions.length === 0 ? (
                    <div className="py-10 text-center text-slate-400 text-sm" data-testid="arcade-empty">No questions for Group {group}, Level {level} yet.</div>
                ) : questions.map((q, idx) => (
                    <div key={q.id} className="p-4" data-testid={`arcade-q-${q.id}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="flex-1">
                                <div className="font-semibold text-slate-900 text-sm"><span className="text-slate-400">{idx + 1}.</span> {q.question}</div>
                                {q.image_url && (
                                    <img src={mediaUrl(q.image_url)} alt="" className="mt-2 max-h-24 rounded-lg border border-slate-200" data-testid={`arcade-q-img-${q.id}`} />
                                )}
                                <div className="grid sm:grid-cols-2 gap-1.5 mt-2">
                                    {q.options.map((o, i) => (
                                        <div key={i} className={`text-xs px-2.5 py-1.5 rounded-lg flex items-center gap-1.5 ${i === q.correct_index ? 'bg-emerald-50 text-emerald-700 font-semibold' : 'bg-slate-50 text-slate-600'}`}>
                                            {i === q.correct_index && <CheckCircle2 className="h-3.5 w-3.5" />}{o}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                                <button onClick={() => setEditing(q)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" data-testid={`arcade-edit-${q.id}`}><Pencil className="h-4 w-4" /></button>
                                <button onClick={() => remove(q.id)} className="p-2 rounded-lg hover:bg-rose-50 text-rose-500" data-testid={`arcade-delete-${q.id}`}><Trash2 className="h-4 w-4" /></button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {editing && <QuestionModal q={editing} onClose={() => setEditing(null)} onSaved={onSaved} />}
        </div>
    );
}

function PulseSettingsCard() {
    const [s, setS] = useState(null);
    const [busy, setBusy] = useState(false);
    const [mascotBusy, setMascotBusy] = useState(false);
    const fileRef = useRef(null);
    const load = useCallback(() => {
        api.get('/admin/pulse-power/settings').then((r) => setS(r.data)).catch(() => setS(null));
    }, []);
    useEffect(() => { load(); }, [load]);
    const save = async () => {
        setBusy(true);
        try {
            const r = await api.put('/admin/pulse-power/settings', {
                time_limit_sec: Number(s.time_limit_sec),
                base_points: Number(s.base_points),
                attempt_point: Number(s.attempt_point),
                blazing_enabled: !!s.blazing_enabled,
                blazing_max: Number(s.blazing_max),
                level_config: Object.fromEntries(Object.entries(s.level_config || {}).map(([k, v]) => [k, {
                    per_correct: Number(v.per_correct), attempt_reward: Number(v.attempt_reward), reward_limit: Number(v.reward_limit),
                }])),
            });
            setS(r.data);
            toast.success('KBC settings saved');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Save failed');
        } finally { setBusy(false); }
    };
    const onPickMascot = async (e) => {
        const file = e.target.files?.[0];
        if (fileRef.current) fileRef.current.value = '';
        if (!file) return;
        if (file.size > 8 * 1024 * 1024) return toast.error('Image must be under 8MB');
        setMascotBusy(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            await api.post('/admin/pulse-power/mascot', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success('Voltmaster image updated');
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Upload failed');
        } finally { setMascotBusy(false); }
    };
    const removeMascot = async () => {
        setMascotBusy(true);
        try {
            await api.delete('/admin/pulse-power/mascot');
            toast.success('Reverted to default Voltmaster image');
            load();
        } catch (err) {
            toast.error('Could not remove image');
        } finally { setMascotBusy(false); }
    };
    if (!s) return null;
    const field = (k, v) => setS({ ...s, [k]: v });
    return (
        <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-5" data-testid="pulse-settings-card">
            <h3 className="font-display font-bold text-slate-900 flex items-center gap-2">
                <Settings className="h-4 w-4 text-brand-blue" /> Kaun Banega Cables-pati — game settings
            </h3>

            {/* Voltmaster host image (Point 8) */}
            <div className="mt-3 flex items-center gap-4 rounded-xl border border-slate-200 p-3" data-testid="pulse-mascot-config">
                <div className="w-16 h-16 rounded-full overflow-hidden ring-2 ring-brand-yellow/60 bg-slate-100 shrink-0">
                    {s.mascot_url
                        ? <img src={mediaUrl(s.mascot_url)} alt="Voltmaster" className="w-full h-full object-cover" data-testid="pulse-mascot-preview" />
                        : <div className="w-full h-full grid place-items-center text-[10px] text-slate-400 text-center px-1">Default image</div>}
                </div>
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-800">Voltmaster host image</div>
                    <div className="text-[11px] text-slate-500">Shown to learners inside the quiz. JPG/PNG/WebP, ≤8MB.</div>
                    <div className="flex items-center gap-2 mt-2">
                        <button type="button" onClick={() => fileRef.current?.click()} disabled={mascotBusy}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-blue text-white text-xs font-semibold hover:bg-brand-blueDark disabled:opacity-60" data-testid="pulse-mascot-upload-btn">
                            <ImagePlus className="h-3.5 w-3.5" /> {mascotBusy ? 'Uploading…' : (s.mascot_url ? 'Replace image' : 'Upload image')}
                        </button>
                        {s.mascot_url && (
                            <button type="button" onClick={removeMascot} disabled={mascotBusy}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-50 text-rose-600 text-xs font-semibold hover:bg-rose-100 disabled:opacity-60" data-testid="pulse-mascot-remove-btn">
                                <X className="h-3.5 w-3.5" /> Use default
                            </button>
                        )}
                    </div>
                    <input ref={fileRef} type="file" accept="image/*" onChange={onPickMascot} className="hidden" data-testid="pulse-mascot-input" />
                </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
                <label className="block">
                    <span className="text-xs font-semibold text-slate-700">Timer (seconds / question)</span>
                    <input type="number" min={5} max={120} value={s.time_limit_sec} onChange={(e) => field('time_limit_sec', e.target.value)}
                        className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" data-testid="pulse-set-timer" />
                </label>
                <label className="block">
                    <span className="text-xs font-semibold text-slate-700">Base points (× level → {s.base_points}–{s.base_points * 10})</span>
                    <input type="number" min={1} max={1000} value={s.base_points} onChange={(e) => field('base_points', e.target.value)}
                        className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" data-testid="pulse-set-base" />
                </label>
                <label className="block">
                    <span className="text-xs font-semibold text-slate-700">Attempt point / question (legacy)</span>
                    <input type="number" min={0} max={100} value={s.attempt_point} onChange={(e) => field('attempt_point', e.target.value)}
                        className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" data-testid="pulse-set-attempt" />
                </label>
                <label className="block">
                    <span className="text-xs font-semibold text-slate-700">Max blazing multiplier</span>
                    <input type="number" min={1} max={10} value={s.blazing_max} disabled={!s.blazing_enabled} onChange={(e) => field('blazing_max', e.target.value)}
                        className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm disabled:bg-slate-50 disabled:text-slate-400" data-testid="pulse-set-blazing-max" />
                </label>
            </div>

            {/* Point 32/33: per-level DIY rewards */}
            <div className="mt-4" data-testid="pulse-level-config">
                <div className="text-xs font-semibold text-slate-700 mb-1.5">Level-wise rewards (DIY) — points per correct answer, one-time attempt reward (awarded once when a learner finishes every question of the level), and the total reward cap per level.</div>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs min-w-[560px]">
                        <thead className="text-slate-500">
                            <tr><th className="text-left py-1 px-2">Level</th><th className="py-1 px-2">Points / correct answer</th><th className="py-1 px-2">Attempt reward (once)</th><th className="py-1 px-2">Total reward limit</th></tr>
                        </thead>
                        <tbody>
                            {Object.keys(s.level_config || {}).map(Number).sort((a, b) => a - b).map((lv) => {
                                const c = s.level_config[String(lv)];
                                const setLC = (k, v) => setS({ ...s, level_config: { ...s.level_config, [String(lv)]: { ...c, [k]: v } } });
                                return (
                                    <tr key={lv} className="border-t border-slate-100" data-testid={`pulse-lc-row-${lv}`}>
                                        <td className="py-1 px-2 font-bold text-slate-700">L{lv}</td>
                                        <td className="py-1 px-2"><input type="number" min={0} value={c.per_correct} onChange={(e) => setLC('per_correct', e.target.value)} className="w-full px-2 py-1 rounded-lg border border-slate-200 text-center" data-testid={`pulse-lc-per-${lv}`} /></td>
                                        <td className="py-1 px-2"><input type="number" min={0} value={c.attempt_reward} onChange={(e) => setLC('attempt_reward', e.target.value)} className="w-full px-2 py-1 rounded-lg border border-slate-200 text-center" data-testid={`pulse-lc-attempt-${lv}`} /></td>
                                        <td className="py-1 px-2"><input type="number" min={0} value={c.reward_limit} onChange={(e) => setLC('reward_limit', e.target.value)} className="w-full px-2 py-1 rounded-lg border border-slate-200 text-center" data-testid={`pulse-lc-limit-${lv}`} /></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="flex items-center justify-between flex-wrap gap-3 mt-3">
                <button onClick={() => field('blazing_enabled', !s.blazing_enabled)} data-testid="pulse-set-blazing-toggle"
                    className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-bold transition ${s.blazing_enabled ? 'bg-brand-red/10 text-brand-red ring-1 ring-brand-red/30' : 'bg-slate-100 text-slate-500'}`}>
                    <Flame className="h-4 w-4" /> Blazing {s.blazing_max}X speed scoring: {s.blazing_enabled ? 'ON' : 'OFF'}
                </button>
                <button onClick={save} disabled={busy}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-blue text-white font-semibold text-sm hover:bg-brand-blueDark disabled:opacity-60" data-testid="pulse-set-save">
                    <Save className="h-4 w-4" /> {busy ? 'Saving…' : 'Save settings'}
                </button>
            </div>
            <p className="text-[11px] text-slate-400 mt-2">
                Blazing speed awards up to {s.blazing_max}X level points in the first 5 seconds, scaling down to 1X in the last 5 seconds. The game always runs to the end of the level.
            </p>
        </div>
    );
}

const CRUSH_TILE_LABELS = {
    propulse_pack: 'ProPulse Pack (+2)', tools: 'Tools (+1)', short_circuit: 'Short-Circuit (−1)',
    bulb: 'Bulb (+1)', switch: 'Switch (+1)', wire: 'Wire (+3)',
};

function PulseCrushSettingsCard() {
    const [s, setS] = useState(null);
    const [busy, setBusy] = useState(false);
    const [tileBusy, setTileBusy] = useState(null);
    const fileRefs = useRef({});
    const load = useCallback(() => {
        api.get('/admin/pulse-crush/settings').then((r) => setS(r.data)).catch(() => setS(null));
    }, []);
    useEffect(() => { load(); }, [load]);
    const save = async () => {
        setBusy(true);
        try {
            const level_config = Object.fromEntries(Object.entries(s.level_config || {}).map(([k, v]) => [k, {
                max_score: Number(v.max_score), slab: Number(v.slab), per_correct: Number(v.per_correct),
            }]));
            const r = await api.put('/admin/pulse-crush/settings', {
                start_moves: Number(s.start_moves), moves_per_correct: Number(s.moves_per_correct),
                max_moves: Number(s.max_moves), attempt_point: Number(s.attempt_point),
                unlock_all: !!s.unlock_all, level_config,
            });
            setS(r.data);
            toast.success('Pulse Crush settings saved');
        } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
        finally { setBusy(false); }
    };
    const onPickTile = async (type, e) => {
        const file = e.target.files?.[0];
        if (fileRefs.current[type]) fileRefs.current[type].value = '';
        if (!file) return;
        if (file.size > 8 * 1024 * 1024) return toast.error('Image must be under 8MB');
        setTileBusy(type);
        try {
            const fd = new FormData(); fd.append('file', file);
            await api.post(`/admin/pulse-crush/tile/${type}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success('Tile image updated'); load();
        } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
        finally { setTileBusy(null); }
    };
    const removeTile = async (type) => {
        setTileBusy(type);
        try { await api.delete(`/admin/pulse-crush/tile/${type}`); toast.success('Reverted to default icon'); load(); }
        catch { toast.error('Could not remove'); } finally { setTileBusy(null); }
    };
    if (!s) return null;
    const field = (k, v) => setS({ ...s, [k]: v });
    const levelsArr = Object.keys(s.level_config || {}).map(Number).sort((a, b) => a - b);
    return (
        <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-5" data-testid="crush-settings-card">
            <h3 className="font-display font-bold text-slate-900 flex items-center gap-2">
                <Settings className="h-4 w-4 text-brand-blue" /> Pulse Crush — game settings
            </h3>
            <p className="text-[11px] text-slate-400 mt-1">Moves-based match-3 (no timer, no lives). Fill each level's score bar to clear it; run out of moves and the level fails.</p>

            {/* Tile images */}
            <div className="mt-3">
                <div className="text-xs font-semibold text-slate-700 mb-2">Tile images (optional — default icons used otherwise)</div>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                    {(s.tiles || []).map((t) => (
                        <div key={t.type} className="rounded-xl border border-slate-200 p-2 text-center" data-testid={`crush-tile-config-${t.type}`}>
                            <div className="w-full aspect-square rounded-lg overflow-hidden bg-slate-100 grid place-items-center mb-1.5">
                                {t.image_url
                                    ? <img src={mediaUrl(t.image_url)} alt={t.type} className="w-full h-full object-cover" data-testid={`crush-tile-preview-${t.type}`} />
                                    : <span className="text-[9px] text-slate-400 px-1">Default icon</span>}
                            </div>
                            <div className="text-[9px] font-bold text-slate-600 leading-tight mb-1">{CRUSH_TILE_LABELS[t.type] || t.type}</div>
                            <button type="button" onClick={() => fileRefs.current[t.type]?.click()} disabled={tileBusy === t.type}
                                className="w-full inline-flex items-center justify-center gap-1 px-1.5 py-1 rounded-md bg-brand-blue text-white text-[10px] font-semibold hover:bg-brand-blueDark disabled:opacity-60"
                                data-testid={`crush-tile-upload-${t.type}`}>
                                <ImagePlus className="h-3 w-3" /> {tileBusy === t.type ? '…' : (t.image_url ? 'Replace' : 'Upload')}
                            </button>
                            {t.image_url && (
                                <button type="button" onClick={() => removeTile(t.type)} disabled={tileBusy === t.type}
                                    className="w-full mt-1 inline-flex items-center justify-center gap-1 px-1.5 py-1 rounded-md bg-rose-50 text-rose-600 text-[10px] font-semibold hover:bg-rose-100"
                                    data-testid={`crush-tile-remove-${t.type}`}>
                                    <X className="h-3 w-3" /> Default
                                </button>
                            )}
                            <input ref={(el) => (fileRefs.current[t.type] = el)} type="file" accept="image/*" onChange={(e) => onPickTile(t.type, e)} className="hidden" />
                        </div>
                    ))}
                </div>
            </div>

            {/* Moves + reward settings */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                <label className="block">
                    <span className="text-xs font-semibold text-slate-700">Start moves</span>
                    <input type="number" min={1} max={200} value={s.start_moves} onChange={(e) => field('start_moves', e.target.value)}
                        className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" data-testid="crush-set-start-moves" />
                </label>
                <label className="block">
                    <span className="text-xs font-semibold text-slate-700">Moves / correct</span>
                    <input type="number" min={0} max={100} value={s.moves_per_correct} onChange={(e) => field('moves_per_correct', e.target.value)}
                        className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" data-testid="crush-set-moves-correct" />
                </label>
                <label className="block">
                    <span className="text-xs font-semibold text-slate-700">Max moves (cap)</span>
                    <input type="number" min={1} max={500} value={s.max_moves} onChange={(e) => field('max_moves', e.target.value)}
                        className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" data-testid="crush-set-max-moves" />
                </label>
                <label className="block">
                    <span className="text-xs font-semibold text-slate-700">Attempt points</span>
                    <input type="number" min={0} max={1000} value={s.attempt_point} onChange={(e) => field('attempt_point', e.target.value)}
                        className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 text-sm" data-testid="crush-set-attempt-point" />
                </label>
            </div>
            <div className="mt-3">
                <button type="button" onClick={() => field('unlock_all', !s.unlock_all)} data-testid="crush-set-unlock-all"
                    className={`inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-sm font-bold ${s.unlock_all ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' : 'bg-slate-100 text-slate-500'}`}>
                    Release all levels: {s.unlock_all ? 'ON' : 'OFF'}
                </button>
            </div>

            {/* Per-level config */}
            <div className="mt-4">
                <div className="text-xs font-semibold text-slate-700 mb-1.5">Per-level configuration</div>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead>
                            <tr className="text-slate-400 text-[10px] uppercase tracking-wide">
                                <th className="text-left py-1 px-2">Level</th>
                                <th className="py-1 px-2">Max score (bar)</th>
                                <th className="py-1 px-2">Slab (per question)</th>
                                <th className="py-1 px-2">Points / correct</th>
                                <th className="py-1 px-2">Questions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {levelsArr.map((lv) => {
                                const c = s.level_config[String(lv)];
                                const setLC = (k, v) => setS({ ...s, level_config: { ...s.level_config, [String(lv)]: { ...c, [k]: v } } });
                                const qCount = Math.max(1, Math.ceil(Number(c.max_score || 1) / Math.max(1, Number(c.slab || 1))));
                                return (
                                    <tr key={lv} className="border-t border-slate-100">
                                        <td className="py-1 px-2 font-bold text-slate-700">L{lv}</td>
                                        <td className="py-1 px-2"><input type="number" min={1} value={c.max_score} onChange={(e) => setLC('max_score', e.target.value)} className="w-full px-2 py-1 rounded-lg border border-slate-200 text-center" data-testid={`crush-lc-max-${lv}`} /></td>
                                        <td className="py-1 px-2"><input type="number" min={1} value={c.slab} onChange={(e) => setLC('slab', e.target.value)} className="w-full px-2 py-1 rounded-lg border border-slate-200 text-center" data-testid={`crush-lc-slab-${lv}`} /></td>
                                        <td className="py-1 px-2"><input type="number" min={1} value={c.per_correct} onChange={(e) => setLC('per_correct', e.target.value)} className="w-full px-2 py-1 rounded-lg border border-slate-200 text-center" data-testid={`crush-lc-per-${lv}`} /></td>
                                        <td className="py-1 px-2 text-center font-bold text-slate-500" data-testid={`crush-lc-qcount-${lv}`}>{qCount}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-3 mt-4">
                <p className="text-[11px] text-slate-400">Released now: <b>{s.released_levels}</b>/5 levels. Schedule: 4 on day 1, +1/day (toggle "Release all" to override).</p>
                <button onClick={save} disabled={busy}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-blue text-white font-semibold text-sm hover:bg-brand-blueDark disabled:opacity-60" data-testid="crush-set-save">
                    <Save className="h-4 w-4" /> {busy ? 'Saving…' : 'Save settings'}
                </button>
            </div>
        </div>
    );
}

function QuestionModal({ q, onClose, onSaved }) {
    const isNew = !q.id;
    const [f, setF] = useState({
        question: q.question || '', options: q.options?.length ? [...q.options] : ['', '', '', ''],
        correct_index: q.correct_index ?? 0, explanation: q.explanation || '',
        group: q.group, level: q.level, game_code: q.game_code,
    });
    const [busy, setBusy] = useState(false);
    const [imgFile, setImgFile] = useState(null);
    const [imgPreview, setImgPreview] = useState(q.image_url ? mediaUrl(q.image_url) : null);
    const [removeImg, setRemoveImg] = useState(false);
    const fileRef = useRef(null);

    const setOpt = (i, v) => { const o = [...f.options]; o[i] = v; setF({ ...f, options: o }); };

    const onPickImg = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 8 * 1024 * 1024) return toast.error('Image must be under 8MB');
        setImgFile(file);
        setImgPreview(URL.createObjectURL(file));
        setRemoveImg(false);
    };

    const clearImg = () => { setImgFile(null); setImgPreview(null); setRemoveImg(true); if (fileRef.current) fileRef.current.value = ''; };

    const save = async () => {
        if (!f.question.trim()) return toast.error('Question text required');
        if (f.options.filter((o) => o.trim()).length < 2) return toast.error('At least 2 options required');
        setBusy(true);
        try {
            let qid = q.id;
            if (isNew) {
                const r = await api.post('/admin/arcade-questions', f);
                qid = r.data.id;
            } else {
                await api.patch(`/admin/arcade-questions/${q.id}`, {
                    question: f.question, options: f.options, correct_index: f.correct_index, explanation: f.explanation,
                });
            }
            if (imgFile && qid) {
                const fd = new FormData();
                fd.append('file', imgFile);
                await api.post(`/admin/arcade-questions/${qid}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            } else if (removeImg && !isNew) {
                await api.delete(`/admin/arcade-questions/${q.id}/image`).catch(() => {});
            }
            toast.success(isNew ? 'Question added' : 'Question updated');
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Save failed');
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="arcade-question-modal">
            <div className="bg-white rounded-3xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <h3 className="font-display font-bold text-xl text-slate-900">{isNew ? 'Add' : 'Edit'} question · Group {f.group} · Level {f.level}</h3>
                <div className="mt-4 space-y-3">
                    <label className="block">
                        <span className="text-xs font-semibold text-slate-700">Question</span>
                        <textarea value={f.question} onChange={(e) => setF({ ...f, question: e.target.value })} rows={2}
                            className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" data-testid="q-text" />
                    </label>

                    {/* Optional question image — shown with the answer options in the game */}
                    <div className="block">
                        <span className="text-xs font-semibold text-slate-700">Image (optional — shown with the options)</span>
                        {imgPreview ? (
                            <div className="mt-1 relative inline-block">
                                <img src={imgPreview} alt="question" className="max-h-40 rounded-xl border border-slate-200" data-testid="q-image-preview" />
                                <button type="button" onClick={clearImg} data-testid="q-image-remove"
                                    className="absolute -top-2 -right-2 w-7 h-7 grid place-items-center rounded-full bg-rose-500 text-white shadow hover:bg-rose-600">
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        ) : (
                            <button type="button" onClick={() => fileRef.current?.click()} data-testid="q-image-btn"
                                className="mt-1 w-full inline-flex items-center justify-center gap-2 px-3 py-3 rounded-xl border-2 border-dashed border-slate-300 text-slate-500 text-sm font-semibold hover:border-brand-blue hover:text-brand-blue">
                                <ImagePlus className="h-4 w-4" /> Upload image
                            </button>
                        )}
                        <input ref={fileRef} type="file" accept="image/*" onChange={onPickImg} className="hidden" data-testid="q-image-input" />
                    </div>

                    <div className="space-y-2">
                        <span className="text-xs font-semibold text-slate-700">Options (select the correct one)</span>
                        {f.options.map((o, i) => (
                            <div key={i} className="flex items-center gap-2">
                                <input type="radio" name="correct" checked={f.correct_index === i} onChange={() => setF({ ...f, correct_index: i })} data-testid={`q-correct-${i}`} />
                                <input value={o} onChange={(e) => setOpt(i, e.target.value)} placeholder={`Option ${i + 1}`}
                                    className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm" data-testid={`q-option-${i}`} />
                            </div>
                        ))}
                    </div>
                    <label className="block">
                        <span className="text-xs font-semibold text-slate-700">Explanation (optional)</span>
                        <textarea value={f.explanation} onChange={(e) => setF({ ...f, explanation: e.target.value })} rows={2}
                            className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" data-testid="q-explanation" />
                    </label>
                </div>
                <div className="flex justify-end gap-2 mt-5">
                    <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-900" data-testid="q-cancel">Cancel</button>
                    <button onClick={save} disabled={busy} className="px-4 py-2 rounded-xl bg-brand-blue text-white text-sm font-semibold disabled:opacity-60" data-testid="q-save">
                        {busy ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
}
